import React from 'react';
import { TooltipItem } from 'chart.js';
import { ILineItemCurrency, ILineItemsData, ISelectedItems } from '@common/interfaces/Common';

const overallSummaryData = (
  selectedLineItems: ISelectedItems,
  data: ILineItemsData[],
  currencies: ILineItemCurrency,
) => {
  const labels = Object.keys(selectedLineItems).filter((label) => selectedLineItems[label].length);

  const calculateTotalRowValue = (element: string) => {
    const value = selectedLineItems[element].reduce((rowAggregator: number, rowValue: number) => {
      return rowAggregator + ((data[rowValue]?.values[element] as number) ?? 0);
    }, 0);
    return value;
  };

  const total = labels.reduce((aggregator: number, element) => {
    const value = calculateTotalRowValue(element);
    return aggregator + value;
  }, 0);

  return [
    {
      label: 'OVERALL COST',
      content: (
        <span className="total-overall">
          {Number(total.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD
        </span>
      ),
    },
    ...labels.map((label) => {
      return {
        label,
        content: (
          <span>
            {Number(calculateTotalRowValue(label).toFixed(2)).toLocaleString('en-US', {
              minimumFractionDigits: 2,
            })}{' '}
            {currencies[label]}
          </span>
        ),
      };
    }),
  ];
};

const getChartProps = (
  selectedLineItems: ISelectedItems,
  data: ILineItemsData[],
  hexColors: string[],
  currencies: ILineItemCurrency,
) => {
  const labels = Object.keys(selectedLineItems).filter((label) => selectedLineItems[label].length);

  return {
    options: {
      plugins: {
        tooltip: {
          padding: 10,
          bodyFont: {
            size: 14,
          },
          callbacks: {
            title(tooltipData: TooltipItem<'doughnut'>[]) {
              return tooltipData[0].label;
            },
            label(tooltipData: TooltipItem<'doughnut'>) {
              return ` ${Number(tooltipData.parsed.toFixed(2)).toLocaleString('en-US', {
                minimumFractionDigits: 2,
              })} ${currencies[tooltipData.label]}`;
            },
          },
        },
      },
      elements: {
        arc: {
          borderWidth: 10,
          borderRadius: 1,
        },
      },
    },
    data: {
      labels,
      datasets: [
        {
          data: labels.map((label) =>
            selectedLineItems[label].reduce((aggregator: number, element) => {
              return aggregator + ((data[element]?.values[label] as number) ?? 0);
            }, 0),
          ),
          borderWidth: 1,
          backgroundColor: hexColors,
          hoverBorderColor: 'white',
          cutout: '80%',
          spacing: labels.length === 1 ? 0 : 5,
          weight: 1,
        },
      ],
    },
  };
};

const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i += 1) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export { overallSummaryData, getChartProps, getRandomColor };
